import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { useClass, Class, Course, Teacher, ClassFormData } from './hooks/useClass';

const ClassEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { fetchClassById, updateClass, fetchCourses, fetchTeachers, isLoading } = useClass();
  
  const [formData, setFormData] = useState<ClassFormData>({
    title: '',
    description: '',
    courseId: '',
    teacherId: '',
    startDate: '',
    endDate: '',
    enrollmentLimit: undefined,
    status: 'upcoming',
  });

  const [originalClass, setOriginalClass] = useState<Class | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      
      setIsDataLoading(true);
      try {
        // Load class data, courses, and teachers in parallel
        const [classData, coursesData, teachersData] = await Promise.all([
          fetchClassById(id),
          fetchCourses(),
          fetchTeachers(),
        ]);

        if (!classData) {
          setError('Class not found');
          return;
        }

        setOriginalClass(classData);
        setCourses(coursesData);
        setTeachers(teachersData);

        // Set form data from loaded class
        setFormData({
          title: classData.title,
          description: classData.description || '',
          courseId: classData.courseId,
          teacherId: classData.teacherId,
          startDate: classData.startDate,
          endDate: classData.endDate,
          enrollmentLimit: classData.enrollmentLimit,
          status: classData.status,
        });
      } catch (err) {
        setError('Failed to load class data');
        console.error(err);
      } finally {
        setIsDataLoading(false);
      }
    };

    loadData();
  }, [id, fetchClassById, fetchCourses, fetchTeachers]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: name === 'enrollmentLimit' ? (value ? parseInt(value, 10) : undefined) : value,
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCourseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const courseId = e.target.value;
    const selectedCourse = courses.find(c => c.id === courseId);
    
    setFormData(prev => ({
      ...prev,
      courseId,
      // Don't auto-update title when editing, let user decide
    }));
    
    if (formErrors.courseId) {
      setFormErrors(prev => ({ ...prev, courseId: '' }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.title?.trim()) {
      errors.title = 'Tên lớp học là bắt buộc';
    }
    
    if (!formData.courseId) {
      errors.courseId = 'Vui lòng chọn khóa học';
    }
    
    if (!formData.teacherId) {
      errors.teacherId = 'Vui lòng chọn giảng viên';
    }
    
    if (!formData.startDate) {
      errors.startDate = 'Ngày bắt đầu là bắt buộc';
    }
    
    if (!formData.endDate) {
      errors.endDate = 'Ngày kết thúc là bắt buộc';
    }
    
    if (formData.startDate && formData.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
      errors.endDate = 'Ngày kết thúc phải sau ngày bắt đầu';
    }
    
    if (formData.enrollmentLimit && formData.enrollmentLimit < 1) {
      errors.enrollmentLimit = 'Giới hạn đăng ký phải lớn hơn 0';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !id) {
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      await updateClass(id, formData);
      navigate(`/lms/classes/${id}`);
    } catch (err) {
      setError('Failed to update class. Please try again.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCourse = courses.find(c => c.id === formData.courseId);

  if (isDataLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Đang tải...</p>
        </div>
      </div>
    );
  }

  if (error && !originalClass) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="text-red-600 mb-4">{error}</div>
          <Link 
            to="/lms/classes"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Quay lại danh sách lớp học
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="px-4 sm:px-6 lg:max-w-6xl lg:mx-auto lg:px-8">
          <div className="py-6 md:flex md:items-center md:justify-between lg:border-t lg:border-gray-200">
            <div className="flex-1 min-w-0">
              <div className="flex items-center">
                <Link 
                  to={`/lms/classes/${id}`}
                  className="mr-4 text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                </Link>
                <div>
                  <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:leading-9 sm:truncate">
                    Chỉnh sửa lớp học
                  </h1>
                  <p className="text-gray-600">{originalClass?.title}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Basic Information */}
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">Thông tin cơ bản</h2>
                
                {/* Course Selection */}
                <div className="grid grid-cols-1 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="courseId">
                      Khóa học <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="courseId"
                      name="courseId"
                      value={formData.courseId}
                      onChange={handleCourseChange}
                      className={`block w-full rounded-md border ${
                        formErrors.courseId ? 'border-red-500' : 'border-gray-300'
                      } px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    >
                      <option value="">Chọn khóa học</option>
                      {courses.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.title} - {course.subject} (Cấp {course.level})
                        </option>
                      ))}
                    </select>
                    {formErrors.courseId && <p className="mt-1 text-sm text-red-600">{formErrors.courseId}</p>}
                  </div>
                </div>

                {/* Course Info Display */}
                {selectedCourse && (
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                    <h3 className="text-sm font-medium text-blue-900 mb-2">Thông tin khóa học đã chọn:</h3>
                    <p className="text-sm text-blue-800 mb-1"><strong>Mô tả:</strong> {selectedCourse.description}</p>
                    <p className="text-sm text-blue-800 mb-1"><strong>Thời lượng:</strong> {selectedCourse.duration.value} {selectedCourse.duration.unit === 'week' ? 'tuần' : selectedCourse.duration.unit === 'month' ? 'tháng' : 'ngày'}</p>
                    <p className="text-sm text-blue-800"><strong>Hình thức:</strong> {selectedCourse.mode === 'blended' ? 'Kết hợp' : selectedCourse.mode === 'live' ? 'Trực tiếp' : 'Tự học'}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Class Title */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="title">
                      Tên lớp học <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Nhập tên lớp học"
                      className={`block w-full rounded-md border ${
                        formErrors.title ? 'border-red-500' : 'border-gray-300'
                      } px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    />
                    {formErrors.title && <p className="mt-1 text-sm text-red-600">{formErrors.title}</p>}
                  </div>

                  {/* Class Description */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="description">
                      Mô tả lớp học
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Nhập mô tả chi tiết về lớp học"
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  {/* Teacher Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="teacherId">
                      Giảng viên <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="teacherId"
                      name="teacherId"
                      value={formData.teacherId}
                      onChange={handleInputChange}
                      className={`block w-full rounded-md border ${
                        formErrors.teacherId ? 'border-red-500' : 'border-gray-300'
                      } px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    >
                      <option value="">Chọn giảng viên</option>
                      {teachers.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.name} ({teacher.role}) - {teacher.department}
                        </option>
                      ))}
                    </select>
                    {formErrors.teacherId && <p className="mt-1 text-sm text-red-600">{formErrors.teacherId}</p>}
                  </div>

                  {/* Enrollment Limit */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="enrollmentLimit">
                      Giới hạn đăng ký
                    </label>
                    <input
                      type="number"
                      id="enrollmentLimit"
                      name="enrollmentLimit"
                      value={formData.enrollmentLimit || ''}
                      onChange={handleInputChange}
                      min="1"
                      placeholder="Không giới hạn"
                      className={`block w-full rounded-md border ${
                        formErrors.enrollmentLimit ? 'border-red-500' : 'border-gray-300'
                      } px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    />
                    {formErrors.enrollmentLimit && <p className="mt-1 text-sm text-red-600">{formErrors.enrollmentLimit}</p>}
                    <p className="mt-1 text-sm text-gray-500">Để trống nếu không muốn giới hạn số lượng học sinh</p>
                  </div>
                </div>
              </div>

              {/* Schedule */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">Lịch học</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="startDate">
                      Ngày bắt đầu <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      className={`block w-full rounded-md border ${
                        formErrors.startDate ? 'border-red-500' : 'border-gray-300'
                      } px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    />
                    {formErrors.startDate && <p className="mt-1 text-sm text-red-600">{formErrors.startDate}</p>}
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="endDate">
                      Ngày kết thúc <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      className={`block w-full rounded-md border ${
                        formErrors.endDate ? 'border-red-500' : 'border-gray-300'
                      } px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
                    />
                    {formErrors.endDate && <p className="mt-1 text-sm text-red-600">{formErrors.endDate}</p>}
                  </div>
                </div>
              </div>

              {/* Status */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">Trạng thái</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="status">
                    Trạng thái lớp học
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="upcoming">Sắp diễn ra</option>
                    <option value="active">Đang diễn ra</option>
                    <option value="completed">Đã hoàn thành</option>
                    <option value="cancelled">Đã hủy</option>
                  </select>
                </div>
              </div>

              {/* Current enrollment info */}
              {originalClass && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Thông tin hiện tại:</h3>
                  <p className="text-sm text-gray-700">
                    <strong>Số học sinh đã đăng ký:</strong> {originalClass.enrollmentCount}
                    {originalClass.enrollmentLimit && ` / ${originalClass.enrollmentLimit}`}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Lưu ý: Thay đổi giới hạn đăng ký có thể ảnh hưởng đến học sinh hiện tại
                  </p>
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <Link
                to={`/lms/classes/${id}`}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Hủy
              </Link>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmitting && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isSubmitting ? 'Đang cập nhật...' : 'Cập nhật lớp học'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClassEditPage; 